import { useRef, type ReactNode } from "react";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { Button } from "../../components/ui/Button";
import { Badge, ErrorText, ProgressBar } from "../../components/ui/feedback";
import { Menu, type MenuItem } from "../../components/ui/Menu";
import { Status } from "../../components/ui/Status";
import { useI18n } from "../../i18n/I18nProvider";
import type { Person } from "../../lib/api-types";
import { formatRegion } from "../../lib/format";
import { PersonThumb } from "./PersonThumb";
import { usePhotoUpload } from "./usePhotoUpload";

export interface RosterRowProps {
  workspaceId: string;
  person: Person;
  changed: boolean;
  lockBusy: boolean;
  onEdit: () => void;
  onCreateLink: () => void;
  onShowLink: (url: string) => void;
  onNewLink: () => void;
  onToggleLock: () => void;
  onToggleSongLock: () => void;
  onDelete: () => void;
  onRestore: () => void;
}

/**
 * One roster row (a label-grid row). Desktop: number, photo, name, song + section, status, action, menu.
 * Tablet: the name and song stack in one cell. Phone: number and name stack, status under, actions last.
 */
export function RosterRow(props: RosterRowProps) {
  const { t, lang } = useI18n();
  const { person, workspaceId, changed } = props;
  const deleted = person.deletedAt !== null;
  const deletedDate = person.deletedAt ? new Date(person.deletedAt).toLocaleDateString(lang) : "";
  const photo = usePhotoUpload(workspaceId, person.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const link = person.link;

  const menuItems: MenuItem[] = [
    { label: t("common.edit"), onSelect: props.onEdit },
    {
      label: person.photoMediaId ? t("roster.replacePhoto") : t("roster.uploadPhoto"),
      onSelect: () => fileInput.current?.click(),
      disabled: photo.isPending,
    },
    {
      label: person.photoLocked ? t("roster.unlockPhoto") : t("roster.lockPhoto"),
      onSelect: props.onToggleLock,
      disabled: props.lockBusy,
    },
    {
      label: person.songLocked ? t("roster.unlockSong") : t("roster.lockSong"),
      onSelect: props.onToggleSongLock,
      disabled: props.lockBusy,
    },
    ...(link ? [{ label: t("roster.regenerate"), onSelect: props.onNewLink }] : []),
    { label: t("common.delete"), onSelect: props.onDelete, tone: "danger" as const },
  ];

  return (
    <li>
      <article
        aria-label={person.name}
        className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 py-3 sm:grid-cols-[3.5rem_3rem_minmax(0,1fr)_auto_2.75rem] sm:gap-x-4 lg:grid-cols-[4rem_3rem_minmax(0,1fr)_minmax(0,1fr)_9rem_auto_2.75rem]"
      >
        <JerseyNumber value={person.number ?? "—"} size="md" />
        <PersonThumb workspaceId={workspaceId} mediaId={person.photoMediaId} name={person.name} />
        <div className="flex min-w-0 flex-col gap-1 lg:contents">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="min-w-0 truncate text-[1.0625rem] font-semibold text-ink">
              {person.name}
            </h3>
            {changed && !deleted ? <Badge tone="accent">{t("roster.changed")}</Badge> : null}
          </div>
          {deleted ? (
            <p className="text-note text-ink-2">{t("roster.deletedOn", { date: deletedDate })}</p>
          ) : (
            <SongLine person={person} />
          )}
          {deleted ? (
            <span aria-hidden="true" className="hidden lg:block" />
          ) : (
            <RowStatus person={person} />
          )}
        </div>
        <div className="col-span-2 flex items-center justify-end gap-2 sm:contents">
          <PrimaryAction
            person={person}
            onCreate={props.onCreateLink}
            onNew={props.onNewLink}
            onShow={props.onShowLink}
            onRestore={props.onRestore}
          />
          {deleted ? null : (
            <Menu label={t("roster.moreActions", { name: person.name })} items={menuItems} />
          )}
        </div>
        {photo.progress !== null ? (
          <div className="col-span-full">
            <ProgressBar fraction={photo.progress} label={t("roster.uploading")} />
          </div>
        ) : null}
        <div className="col-span-full">
          <ErrorText error={photo.error} />
        </div>
        <input
          ref={fileInput}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          className="hidden"
          aria-label={t("roster.uploadPhoto")}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) photo.upload(file);
          }}
        />
      </article>
    </li>
  );
}

function SongLine({ person }: { person: Person }): ReactNode {
  const { t } = useI18n();
  if (!person.songMediaId) {
    return <p className="text-note text-ink-3">{t("roster.noSong")}</p>;
  }
  return (
    <p className="min-w-0 truncate text-note text-ink-2">
      {person.songLabel ?? ""}{" "}
      <span className="tabular-nums text-ink-3">
        {formatRegion(person.startMs, person.lengthS)}
      </span>
    </p>
  );
}

function RowStatus({ person }: { person: Person }): ReactNode {
  const { t } = useI18n();
  const link = person.link;
  const locked = person.songLocked || person.photoLocked;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {locked ? <Status tone="live">{t("roster.statusLocked")}</Status> : null}
      {link === null ? <Status tone="idle">{t("roster.statusNoLink")}</Status> : null}
      {link && link.openedAt ? <Status tone="linked">{t("roster.statusOpened")}</Status> : null}
      {link && !link.openedAt ? <Status tone="idle">{t("roster.notOpened")}</Status> : null}
    </div>
  );
}

interface PrimaryActionProps {
  person: Person;
  onCreate: () => void;
  onNew: () => void;
  onShow: (url: string) => void;
  onRestore: () => void;
}

/** The one action a row offers: restore, create a link, a new link, or share the existing one. */
function PrimaryAction({ person, onCreate, onNew, onShow, onRestore }: PrimaryActionProps) {
  const { t } = useI18n();
  if (person.deletedAt !== null) {
    return (
      <Button variant="secondary" onClick={onRestore}>
        {t("common.restore")}
      </Button>
    );
  }
  const link = person.link;
  if (link === null) {
    return (
      <Button variant="secondary" onClick={onCreate}>
        {t("roster.linkCreate")}
      </Button>
    );
  }
  if (link.url === null || link.unavailable) {
    return <Button onClick={onNew}>{t("roster.newLink")}</Button>;
  }
  const url = link.url;
  return <Button onClick={() => onShow(url)}>{t("roster.shareLink")}</Button>;
}
